// src/components/auth/AdminLogin.jsx
// Pantalla de login para administradores
// Diseño consistente con la UI de RealtyBid (slate/amber)

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Building2, Mail, Lock, Eye, EyeOff, AlertCircle, ArrowRight } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

/**
 * Pantalla de login para administradores
 * - Login con email/password via Supabase Auth
 * - Manejo de errores con mensajes claros
 * - Enlace a recuperación de contraseña
 * - Redirige a /admin tras login exitoso (o a la ruta original)
 */
const AdminLogin = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { signIn, resetPassword, user, loading: authLoading } = useAuth();

  // Estados del formulario
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Estado para recuperación de contraseña
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);

  // Obtener la ruta a la que redirigir después del login
  const from = location.state?.from?.pathname || '/admin';

  // Si ya está autenticado, redirigir
  useEffect(() => {
    if (user && !authLoading) {
      navigate(from, { replace: true });
    }
  }, [user, authLoading, navigate, from]);

  // ============================================
  // HANDLER: Submit del formulario de login
  // ============================================
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Validaciones básicas
    if (!email.trim()) {
      setError('Por favor, introduce tu email');
      setLoading(false);
      return;
    }

    if (!password) {
      setError('Por favor, introduce tu contraseña');
      setLoading(false);
      return;
    }

    // Intentar login
    const result = await signIn(email.trim(), password);

    if (result.success) {
      // Redirigir a la ruta original o a /admin
      navigate(from, { replace: true });
    } else {
      // Mostrar error amigable
      setError(getErrorMessage(result.error));
    }

    setLoading(false);
  };

  // ============================================
  // HANDLER: Recuperación de contraseña
  // ============================================
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!email.trim()) {
      setError('Por favor, introduce tu email para recuperar la contraseña');
      return;
    }

    setResetLoading(true);
    const result = await resetPassword(email.trim());
    
    if (result.success) {
      setResetEmailSent(true);
    } else {
      setError(getErrorMessage(result.error));
    }
    
    setResetLoading(false);
  };

  // ============================================
  // HELPER: Traducir errores de Supabase
  // ============================================
  const getErrorMessage = (errorMsg) => {
    if (!errorMsg) return 'Error desconocido';
    
    const errorLower = errorMsg.toLowerCase();
    
    if (errorLower.includes('invalid login credentials') || 
        errorLower.includes('invalid_credentials')) {
      return 'Email o contraseña incorrectos';
    }
    if (errorLower.includes('email not confirmed')) {
      return 'Por favor, confirma tu email antes de iniciar sesión';
    }
    if (errorLower.includes('too many requests')) {
      return 'Demasiados intentos. Por favor, espera unos minutos';
    }
    if (errorLower.includes('network')) {
      return 'Error de conexión. Verifica tu conexión a internet';
    }
    
    return 'Error al iniciar sesión. Por favor, inténtalo de nuevo';
  };

  // ============================================
  // RENDER: Mensaje de email enviado (recuperación)
  // ============================================
  if (resetEmailSent) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <div className="bg-slate-800/50 backdrop-blur-xl rounded-2xl p-8 max-w-md w-full border border-slate-700/50 text-center">
          <div className="bg-green-500/10 p-4 rounded-xl inline-block mb-4">
            <Mail className="w-12 h-12 text-green-400" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">Email Enviado</h2>
          <p className="text-slate-400 mb-6">
            Hemos enviado un enlace de recuperación a <span className="text-white font-medium">{email}</span>. 
            Revisa tu bandeja de entrada.
          </p>
          <button
            onClick={() => {
              setShowForgotPassword(false);
              setResetEmailSent(false);
            }}
            className="text-amber-400 hover:text-amber-300 font-medium"
          >
            Volver al login
          </button>
        </div>
      </div>
    );
  }

  // ============================================
  // RENDER: Formulario de recuperación de contraseña
  // ============================================
  if (showForgotPassword) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <div className="bg-slate-800/50 backdrop-blur-xl rounded-2xl p-8 max-w-md w-full border border-slate-700/50">
          <div className="flex justify-center mb-6">
            <div className="bg-gradient-to-br from-amber-400 to-amber-600 p-4 rounded-xl">
              <Building2 className="w-8 h-8 text-slate-900" />
            </div>
          </div>
          
          <h1 className="text-2xl font-bold text-white text-center mb-2">
            Recuperar Contraseña
          </h1>
          <p className="text-slate-400 text-center mb-6">
            Introduce tu email y te enviaremos un enlace para restablecer tu contraseña
          </p>

          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-4 flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
              <span className="text-red-400 text-sm">{error}</span>
            </div>
          )}

          <form onSubmit={handleForgotPassword}>
            <div className="mb-4">
              <label className="block text-slate-300 text-sm font-medium mb-2">
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-500" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="tu@email.com"
                  className="w-full bg-slate-900/50 border border-slate-700 rounded-lg pl-10 pr-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-amber-500 transition-colors"
                  disabled={resetLoading}
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={resetLoading}
              className="w-full bg-gradient-to-r from-amber-500 to-amber-600 text-slate-900 font-semibold py-3 rounded-lg hover:from-amber-600 hover:to-amber-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {resetLoading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-slate-900"></div>
                  Enviando...
                </>
              ) : (
                <>
                  Enviar Enlace
                  <ArrowRight className="w-5 h-5" />
                </>
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={() => {
                setShowForgotPassword(false);
                setError('');
              }}
              className="text-slate-400 hover:text-white text-sm"
            >
              ← Volver al login
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ============================================
  // RENDER: Formulario de login principal
  // ============================================
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="bg-slate-800/50 backdrop-blur-xl rounded-2xl p-8 max-w-md w-full border border-slate-700/50">
        {/* Logo y título */}
        <div className="flex justify-center mb-6">
          <div className="bg-gradient-to-br from-amber-400 to-amber-600 p-4 rounded-xl">
            <Building2 className="w-8 h-8 text-slate-900" />
          </div>
        </div>
        
        <h1 className="text-2xl font-bold text-white text-center mb-2">
          Panel de Administración
        </h1>
        <p className="text-slate-400 text-center mb-6">
          Inicia sesión para continuar
        </p>

        {/* Mensaje de error */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-4 flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
            <span className="text-red-400 text-sm">{error}</span>
          </div>
        )}

        {/* Formulario */}
        <form onSubmit={handleSubmit}>
          {/* Email */}
          <div className="mb-4">
            <label className="block text-slate-300 text-sm font-medium mb-2">
              Email
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-500" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="tu@email.com"
                className="w-full bg-slate-900/50 border border-slate-700 rounded-lg pl-10 pr-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-amber-500 transition-colors"
                disabled={loading}
                autoComplete="email"
              />
            </div>
          </div>

          {/* Password */}
          <div className="mb-6">
            <label className="block text-slate-300 text-sm font-medium mb-2">
              Contraseña
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-500" />
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full bg-slate-900/50 border border-slate-700 rounded-lg pl-10 pr-12 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-amber-500 transition-colors"
                disabled={loading}
                autoComplete="current-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-500 hover:text-slate-300"
              >
                {showPassword ? (
                  <EyeOff className="w-5 h-5" />
                ) : (
                  <Eye className="w-5 h-5" />
                )}
              </button>
            </div>
          </div>

          {/* Botón submit */}
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-amber-500 to-amber-600 text-slate-900 font-semibold py-3 rounded-lg hover:from-amber-600 hover:to-amber-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-slate-900"></div>
                Iniciando sesión...
              </>
            ) : (
              'Acceder'
            )}
          </button>
        </form>

        {/* Enlace olvidé contraseña */}
        <div className="mt-4 text-center">
          <button
            onClick={() => {
              setShowForgotPassword(true);
              setError('');
            }}
            className="text-slate-400 hover:text-amber-400 text-sm transition-colors"
          >
            ¿Olvidaste tu contraseña?
          </button>
        </div>

        {/* Indicador de conexión */}
        <div className="mt-6 pt-6 border-t border-slate-700/50">
          <div className="flex items-center justify-center gap-2 text-xs text-slate-500">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
            Sistema de autenticación seguro
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;